import { forEachOfArray, pushToArray, shiftArray, sizeOfArray, unshiftArray } from "@acng/frontend-bounty";
const logName = "discovery/stream";
/**
 * @template T
 * @typedef Stream
 * @property {number} limit
 * @property {Array<T>} queue
 * @property {(newItems: Array<T>, newLimit?: number) => void} streamReset
 * @property {(stubCount: number, claimCount: number) => Promise<T[]>} get
 * @property {(items: T[]) => void} unget
 * @property {() => void} abort
 * @property {() => void} destroy
 * @property {() => void} reset
 */
/**
 * @template T
 * @param {Array<T>} initialQueue
 * @param {(offset: number) => Promise<T[]>} [callback]
 * @param {number} [initialLimit]
 * @returns {Stream<T>}
 */
export function createStream(initialQueue, callback, initialLimit) {
    /** @private */
    let aborted = false;
    let offset = 0;
    let queue = initialQueue;
    let limit = initialLimit ?? (callback ? Infinity : sizeOfArray(initialQueue));
    let limitReached = false;
    DEBUG: console.debug(`${logName} created`, { queue, callback, limit });
    const pull = callback ?? (async () => []);
    /**
     * @param {Array<T>} newItems
     * @param {number} [newLimit]
     */
    const resetItems = (newItems, newLimit) => {
        queue = newItems;
        offset = 0;
        limit = newLimit ?? (callback ? Infinity : sizeOfArray(queue));
        aborted = false;
        limitReached = false;
    };
    /**
     * @param {number} stubCount - can differ from the internal offset because...
     * @param {number} claimCount
     * @returns {Promise<Array<T>>}
     * TODO count & discardedCount
     */
    const getItems = async (stubCount, claimCount) => {
        /** @type {Array<T>} */
        const stubQueue = [];
        const refQueue = queue;
        for (let i = stubCount; i < claimCount; i++) {
            let data = shiftArray(queue);
            if (!data) {
                if (limitReached || aborted) {
                    break;
                }
                const more = await pull(offset);
                DEBUG: console.debug(`${logName} pulled`, { more, offset, limit });
                if (refQueue !== queue) {
                    DEBUG: console.warn(`${logName} aborted at index ${i}`);
                    ungetItems(stubQueue);
                    return [];
                }
                offset += sizeOfArray(more);
                pushToArray(queue, ...more);
                if (claimCount == Infinity) {
                    claimCount = sizeOfArray(more);
                    DEBUG: console.warn(`${logName} pull with adjusted claim`, { claimCount });
                }
                data = shiftArray(queue);
                if (!data) {
                    limit = i;
                    limitReached = true;
                    DEBUG: console.info(`${logName} claim - data limit reached at index ${i}`);
                    break;
                }
            }
            pushToArray(stubQueue, data);
        }
        return stubQueue;
    };
    /** @param {Array<T>} items */
    const ungetItems = (items) => forEachOfArray(items, (item) => unshiftArray(queue, item));
    const abort = () => void (aborted = true);
    return {
        get limit() {
            return limit;
        },
        get queue() {
            return queue; // old dataQueue
        },
        get: getItems,
        unget: ungetItems,
        streamReset: resetItems,
        abort: abort,
        destroy: abort,
        reset: () => resetItems([]),
    };
}
